import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditCard, Users } from 'lucide-react'
import PageLayout from '../../components/layout/page-layout'
import { Container } from '../../components/ui/container'
import { Badge } from '../../components/ui/badge'
import { useAuth } from '../../auth/auth.context'
import { moduleService, paymentService, userService } from '../../services'
import type { PaymentPackage } from '../../services'
import { useLocale } from '../../hooks/use-locale'
import { FEATURES } from '../../config/features.config'
import { subscriptionStatusToText } from '../../helpers/enum-to-text'
import { parseMarketingPlan, findPackageForMarketingPlan } from '../billing/marketing-plan'
import { useMarketingPlan } from '../billing/use-marketing-plan'
import { redirectToCheckoutForPendingPlan } from '../billing/pending-checkout'

type RankedPackage = PaymentPackage & { tier: number }
type Limits = {
  maxLicenses?: number
}
type PackageMarketingCopy = {
  tagline: string
  description: string
  highlights: readonly string[]
}

const ENTERPRISE_CONTACT_EMAIL = 'kontakt@start-smart.tech'

function buildEnterpriseMailto(mailSubject: string) {
  return `mailto:${ENTERPRISE_CONTACT_EMAIL}?subject=${encodeURIComponent(mailSubject)}`
}

function getPackageMarketingCopy(
  t: (key: string) => string,
  pkg: PaymentPackage
): PackageMarketingCopy {
  const licenses = pkg.maxLicenses || 0

  if (licenses === 3) {
    return {
      tagline: t('subscription.packages.tier3.tagline'),
      description: t('subscription.packages.tier3.description'),
      highlights: [
        t('subscription.packages.tier3.highlightOnboardingBasics'),
        ...(FEATURES.timeTracking ? [t('subscription.packages.tier3.highlightTimeTracking')] : []),
        t('subscription.packages.tier3.highlightFirstModules'),
      ],
    }
  }

  if (licenses === 10) {
    return {
      tagline: t('subscription.packages.tier10.tagline'),
      description: t('subscription.packages.tier10.description'),
      highlights: [
        t('subscription.packages.tier10.highlightEmployees'),
        t('subscription.packages.tier10.highlightWorkflows'),
        t('subscription.packages.tier10.highlightCentralContent'),
      ],
    }
  }

  if (licenses === 25) {
    return {
      tagline: t('subscription.packages.tier25.tagline'),
      description: t('subscription.packages.tier25.description'),
      highlights: [
        t('subscription.packages.tier25.highlightTeams'),
        t('subscription.packages.tier25.highlightRoleModules'),
        t('subscription.packages.tier25.highlightScalable'),
      ],
    }
  }

  if (licenses === 50) {
    return {
      tagline: t('subscription.packages.tier50.tagline'),
      description: t('subscription.packages.tier50.description'),
      highlights: [
        t('subscription.packages.tier50.highlightEmployees'),
        t('subscription.packages.tier50.highlightCrossTeam'),
        t('subscription.packages.tier50.highlightStructure'),
      ],
    }
  }

  return {
    tagline: t('subscription.packages.defaultTagline'),
    description: t('subscription.packages.defaultDescription'),
    highlights: [
      t('subscription.packages.defaultHighlights.onboarding'),
      ...(FEATURES.timeTracking ? [t('subscription.packages.defaultHighlights.timeTracking')] : []),
      t('subscription.packages.defaultHighlights.internalModules'),
    ],
  }
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ')
}

function getUsagePercentage(value: number, limit?: number) {
  if (!limit || limit <= 0) return 0
  return Math.min(100, Math.round((value / limit) * 100))
}

function findPackageByReferences(
  packages: RankedPackage[],
  refs: { packageId?: string; priceId?: string }
) {
  for (const id of [refs.packageId, refs.priceId].filter(Boolean)) {
    const byId = packages.find(pkg => pkg.id === id)
    if (byId) return byId
  }

  if (refs.priceId) {
    return packages.find(pkg => pkg.stripePriceId === refs.priceId) || null
  }

  return null
}

function getStatusVariant(status?: string): 'default' | 'success' | 'warning' | 'error' {
  const normalized = (status || '').toLowerCase()
  if (normalized === 'active' || normalized === 'trialing') return 'success'
  if (normalized === 'past_due' || normalized === 'unpaid') return 'warning'
  if (normalized === 'canceled' || normalized === 'incomplete_expired') return 'error'
  return 'default'
}

function SoftPanel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        'rounded-3xl border border-primary-200/70 bg-white/90 shadow-[0_16px_50px_rgba(56,189,248,0.10)] backdrop-blur-xl',
        className
      )}
    >
      {children}
    </div>
  )
}

function SectionHeader({
  title,
  description,
  right,
}: {
  title: string
  description?: string
  right?: ReactNode
}) {
  return (
    <div className='flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
      <div>
        <h2 className='text-lg font-semibold text-primary-900'>{title}</h2>
        {description && <p className='mt-1 text-sm text-neutral-500'>{description}</p>}
      </div>
      {right}
    </div>
  )
}

function UsageCard({
  icon,
  label,
  value,
  limit,
}: {
  icon: ReactNode
  label: string
  value: number
  limit?: number
}) {
  const percentage = getUsagePercentage(value, limit)

  return (
    <SoftPanel className='p-5 transition duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-[0_20px_56px_rgba(14,165,233,0.14)]'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 ring-1 ring-primary-100'>
            {icon}
          </div>
          <div className='text-sm font-medium text-neutral-600'>{label}</div>
        </div>
        {limit ? (
          <span className='rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-700 ring-1 ring-primary-100'>
            {percentage}%
          </span>
        ) : null}
      </div>

      <div className='mt-4 flex items-end gap-1 text-primary-900'>
        <span className='text-3xl font-semibold tracking-tight'>{value}</span>
        <span className='pb-1 text-sm text-neutral-400'>/ {limit ?? '—'}</span>
      </div>

      <div className='mt-4 h-1.5 overflow-hidden rounded-full bg-primary-50'>
        <div
          className='h-full rounded-full bg-gradient-to-r from-primary-300 to-info-500 transition-all duration-500'
          style={{ width: `${percentage}%` }}
        />
      </div>
    </SoftPanel>
  )
}

function FeaturePill({ label }: { label: string }) {
  return (
    <div className='rounded-2xl border border-primary-100 bg-primary-50/50 px-3 py-2.5 text-sm font-medium text-primary-900'>
      {label}
    </div>
  )
}

type AutoCheckoutPhase = 'idle' | 'starting' | 'done' | 'failed' | 'preselect' | 'unavailable'

export default function SubscriptionPage() {
  const { t } = useTranslation()
  const { formatDate, formatNumber } = useLocale()
  const { user, company } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const marketingPlan = useMarketingPlan()
  const queryPlan = parseMarketingPlan(searchParams.get('plan'))
  const [autoCheckoutPhase, setAutoCheckoutPhase] = useState<AutoCheckoutPhase>(
    queryPlan ? 'starting' : 'idle'
  )

  const formatPrice = (amount: number, currency: string) => {
    if (Number(amount) === 0) return t('common.free')

    return formatNumber(Number(amount), {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase(),
      maximumFractionDigits: 2,
    })
  }

  const formatSubscriptionDate = (value?: string) => {
    if (!value) return t('common.dash')
    return formatDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const formatBillingInterval = (pkg: PaymentPackage) => {
    if (Number(pkg.amount) === 0) return null
    if (pkg.billingInterval === 'year') return t('common.perYear')
    return t('common.perMonth')
  }

  const {
    data: packages = [],
    isLoading: packagesLoading,
    error: packagesError,
  } = useQuery({
    queryKey: ['payment-packages', 'active'],
    queryFn: () => paymentService.listPackages(),
  })

  const {
    data: subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useQuery({
    queryKey: ['company-subscription', user?.companyId],
    queryFn: () => paymentService.getSubscription(),
    enabled: !!user,
  })

  const {
    data: invoices = [],
    isLoading: invoicesLoading,
    error: invoicesError,
  } = useQuery({
    queryKey: ['company-invoices', user?.companyId],
    queryFn: () => paymentService.listInvoices(),
    enabled: !!user,
  })

  const { data: modulesData } = useQuery({
    queryKey: ['company-modules', user?.companyId],
    queryFn: () => moduleService.getModules({ companyId: user?.companyId }),
    enabled: !!user?.companyId,
  })

  const { data: employeesData } = useQuery({
    queryKey: ['company-employees', user?.companyId],
    queryFn: () => userService.getUsers({ companyId: user?.companyId, limit: 100 }),
    enabled: !!user?.companyId,
  })

  const startCheckoutMutation = useMutation({
    mutationFn: (packageId: string) => {
      return paymentService.createCheckoutSession({
        packageId,
      })
    },
    onSuccess: response => {
      window.location.assign(response.url)
    },
  })

  useEffect(() => {
    if (!queryPlan) return
    let active = true
    setAutoCheckoutPhase('starting')
    void redirectToCheckoutForPendingPlan().then(result => {
      if (!active) return
      if (result === 'redirected') {
        setAutoCheckoutPhase('done')
        return
      }
      if (result === 'preselect') {
        setAutoCheckoutPhase('preselect')
        return
      }
      if (result === 'unavailable') {
        setAutoCheckoutPhase('unavailable')
        return
      }
      if (result === 'no-plan') {
        setAutoCheckoutPhase('idle')
        return
      }
      setAutoCheckoutPhase('failed')
    })
    return () => {
      active = false
    }
  }, [queryPlan])

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => paymentService.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-subscription', user?.companyId] })
      queryClient.invalidateQueries({ queryKey: ['company', user?.companyId] })
    },
  })

  const changePackageMutation = useMutation({
    mutationFn: (packageId: string) => paymentService.changePackage({ targetPackageId: packageId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-subscription', user?.companyId] })
      queryClient.invalidateQueries({ queryKey: ['company', user?.companyId] })
    },
  })

  const cancelPendingPackageChangeMutation = useMutation({
    mutationFn: () => paymentService.cancelPendingPackageChange(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-subscription', user?.companyId] })
      queryClient.invalidateQueries({ queryKey: ['company', user?.companyId] })
    },
  })

  const limits = (company as any)?.limits as Limits | undefined

  const moduleCount = modulesData?.modules?.length || 0
  const employeeCount = (employeesData?.users || []).filter(
    employee => employee.role === 'EMPLOYEE'
  ).length

  const rankedPackages = useMemo<RankedPackage[]>(
    () =>
      [...packages]
        .sort((a, b) => Number(a.amount) - Number(b.amount))
        .map((pkg, index) => ({ ...pkg, tier: index + 1 })),
    [packages]
  )
  const freePackage = useMemo(
    () => rankedPackages.find(pkg => Number(pkg.amount) === 0),
    [rankedPackages]
  )

  const activePackage = useMemo(() => {
    const fromSubscription = findPackageByReferences(rankedPackages, {
      packageId: subscription?.packageId,
      priceId: subscription?.stripePriceId,
    })
    if (fromSubscription) return fromSubscription

    const fromCompany = findPackageByReferences(rankedPackages, {
      packageId: company?.subscriptionPackageId,
      priceId: company?.subscriptionPriceId,
    })
    if (fromCompany) return fromCompany

    const byCompanyLimits = rankedPackages.find(pkg => {
      return limits?.maxLicenses === pkg.maxLicenses
    })

    return byCompanyLimits || freePackage || rankedPackages[0] || null
  }, [
    rankedPackages,
    subscription?.packageId,
    subscription?.stripePriceId,
    company?.subscriptionPackageId,
    company?.subscriptionPriceId,
    limits,
    freePackage,
  ])

  const pendingPackageChange = useMemo(
    () =>
      subscription?.pendingStripePriceId
        ? rankedPackages.find(pkg => pkg.stripePriceId === subscription.pendingStripePriceId) ||
          null
        : null,
    [rankedPackages, subscription?.pendingStripePriceId]
  )

  const activePlanName = useMemo(() => {
    if (subscription) return activePackage?.name || t('subscription.activeStripePlan')
    return activePackage?.name || t('subscription.activePackage')
  }, [subscription, activePackage, t])

  const globalError =
    packagesError ||
    subscriptionError ||
    invoicesError ||
    startCheckoutMutation.error ||
    cancelSubscriptionMutation.error ||
    changePackageMutation.error ||
    cancelPendingPackageChangeMutation.error

  const openingCheckout = autoCheckoutPhase === 'starting' || autoCheckoutPhase === 'done'
  const marketingPackage = useMemo(
    () => (marketingPlan ? findPackageForMarketingPlan(rankedPackages, marketingPlan) : null),
    [marketingPlan, rankedPackages]
  )

  return (
    <PageLayout
      isLoading={packagesLoading || subscriptionLoading || openingCheckout}
      error={globalError}
      loadingMessage={
        openingCheckout ? t('subscription.checkoutRedirecting') : t('subscription.loading')
      }
      errorMessage={t('subscription.loadError')}
    >
      <Container>
        <div className='relative overflow-hidden py-2'>
          <div className='pointer-events-none absolute inset-x-[-6rem] top-[-6rem] -z-10 h-80 bg-[radial-gradient(circle_at_top_left,rgba(125,211,252,0.24),transparent_32%),radial-gradient(circle_at_85%_15%,rgba(96,165,250,0.18),transparent_28%)]' />
          <div className='pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(rgba(186,230,253,0.25)_1px,transparent_1px),linear-gradient(90deg,rgba(186,230,253,0.25)_1px,transparent_1px)] bg-[size:44px_44px] opacity-40 [mask-image:linear-gradient(180deg,black,transparent_70%)]' />

          <div className='space-y-8'>
            <div className='flex flex-col gap-4 md:flex-row md:items-end md:justify-between'>
              <div>
                <span className='inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-semibold tracking-wide text-primary-700'>
                  {t('subscription.badge')}
                </span>
                <h1 className='mt-4 text-3xl font-semibold tracking-tight text-primary-900 md:text-4xl'>
                  {t('subscription.title')}
                </h1>
                <p className='mt-2 max-w-2xl text-sm leading-6 text-neutral-500 md:text-base'>
                  {t('subscription.subtitle')}
                </p>
              </div>
            </div>

            <SoftPanel className='overflow-hidden'>
              <div className='grid gap-6 p-6 lg:grid-cols-[1fr_auto] lg:items-center'>
                <div>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span className='rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700 ring-1 ring-primary-100'>
                      {t('subscription.activeSubscription')}
                    </span>
                    {subscription ? (
                      <Badge
                        title={subscriptionStatusToText(subscription.status)}
                        variant={getStatusVariant(subscription.status)}
                      />
                    ) : (
                      <span className='rounded-full bg-neutral-50 px-3 py-1 text-xs font-semibold text-neutral-500 ring-1 ring-neutral-100'>
                        {t('subscription.withoutStripe')}
                      </span>
                    )}
                  </div>

                  <div className='mt-4 flex flex-col gap-2 sm:flex-row sm:items-end'>
                    <h2 className='text-2xl font-semibold text-primary-900'>{activePlanName}</h2>
                    {activePackage && (
                      <div className='text-sm text-neutral-500'>
                        {formatPrice(Number(activePackage.amount), activePackage.currency)}
                        {formatBillingInterval(activePackage)}
                      </div>
                    )}
                  </div>

                  {subscription && (
                    <p className='mt-2 text-sm text-neutral-500'>
                      {t('common.periodRuntime', {
                        start: formatSubscriptionDate(subscription.currentPeriodStart),
                        end: formatSubscriptionDate(subscription.currentPeriodEnd),
                      })}
                    </p>
                  )}

                  {pendingPackageChange && subscription?.pendingPackageChangeAt && (
                    <div className='mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
                      {t('common.pendingPackageChange', {
                        name: pendingPackageChange.name,
                        date: formatSubscriptionDate(subscription.pendingPackageChangeAt),
                      })}
                    </div>
                  )}
                </div>

                {subscription && pendingPackageChange ? (
                  <button
                    onClick={() => cancelPendingPackageChangeMutation.mutate()}
                    disabled={cancelPendingPackageChangeMutation.isPending}
                    className='inline-flex items-center justify-center rounded-full border border-rose-200 bg-rose-50 px-5 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    {cancelPendingPackageChangeMutation.isPending
                      ? t('subscription.removingChange')
                      : t('subscription.cancelPendingChange')}
                  </button>
                ) : subscription &&
                  !subscription.cancelAtPeriodEnd &&
                  !company?.subscriptionCurrentPeriodEnd ? (
                  <button
                    onClick={() => cancelSubscriptionMutation.mutate()}
                    disabled={cancelSubscriptionMutation.isPending}
                    className='inline-flex items-center justify-center rounded-full border border-rose-200 bg-rose-50 px-5 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    {cancelSubscriptionMutation.isPending
                      ? t('subscription.cancelling')
                      : t('subscription.cancelSubscription')}
                  </button>
                ) : null}
              </div>
            </SoftPanel>

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <UsageCard
                icon={<Users className='h-4 w-4' />}
                label={t('common.employees')}
                value={employeeCount}
                limit={limits?.maxLicenses}
              />
              <UsageCard
                icon={<CreditCard className='h-4 w-4' />}
                label={t('subscription.moduleLicenses')}
                value={moduleCount}
                limit={limits?.maxLicenses}
              />
            </div>

            <section className='space-y-4'>
              {marketingPlan && autoCheckoutPhase === 'failed' && (
                <div
                  data-testid='marketing-checkout-error'
                  className='rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900'
                >
                  {t('subscription.marketingCheckoutFailed', { licenses: marketingPlan })}
                </div>
              )}
              {marketingPlan && autoCheckoutPhase === 'unavailable' && (
                <div
                  data-testid='marketing-checkout-error'
                  className='rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900'
                >
                  {t('subscription.marketingPlanUnavailable', { licenses: marketingPlan })}
                </div>
              )}
              {marketingPlan && autoCheckoutPhase === 'preselect' && (
                <div className='rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm leading-6 text-sky-900'>
                  {t('subscription.marketingPlanReady', { licenses: marketingPlan })}
                </div>
              )}
              <SectionHeader
                title={t('subscription.packagesTitle')}
                description={t('subscription.packagesDescription')}
                right={
                  <span className='hidden rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-neutral-500 ring-1 ring-primary-100 sm:inline-flex'>
                    {t('common.packagesAvailable', { count: rankedPackages.length + 1 })}
                  </span>
                }
              />

              <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3'>
                {rankedPackages.map(pkg => {
                  const isCurrentPlan = pkg.id === activePackage?.id
                  const isMarketingSelection = pkg.id === marketingPackage?.id
                  const isDowngrade = !!activePackage && pkg.tier < activePackage.tier
                  const isFreePackage = Number(pkg.amount) === 0
                  const isPendingChangeTarget =
                    Boolean(subscription?.pendingStripePriceId) &&
                    pkg.stripePriceId === subscription?.pendingStripePriceId
                  const isBuyingThisPackage =
                    startCheckoutMutation.isPending && startCheckoutMutation.variables === pkg.id
                  const isChangingThisPackage =
                    changePackageMutation.isPending && changePackageMutation.variables === pkg.id
                  const packageCopy = getPackageMarketingCopy(t, pkg)
                  const forceCheckout =
                    isMarketingSelection && autoCheckoutPhase === 'failed' && !isDowngrade

                  const buttonDisabled =
                    isCurrentPlan && !forceCheckout
                      ? true
                      : isPendingChangeTarget
                        ? true
                        : isDowngrade
                          ? isChangingThisPackage
                          : isBuyingThisPackage

                  const onClick = () => {
                    if (isDowngrade) {
                      changePackageMutation.mutate(pkg.id)
                      return
                    }

                    startCheckoutMutation.mutate(pkg.id)
                  }

                  let buttonText = t('subscription.choosePackage')
                  if (isCurrentPlan && !forceCheckout) {
                    buttonText = t('subscription.currentPackage')
                  } else if (isPendingChangeTarget) {
                    buttonText = t('subscription.changeScheduled')
                  } else if (isDowngrade) {
                    buttonText = isChangingThisPackage
                      ? t('subscription.planningDowngrade')
                      : t('subscription.chooseDowngrade')
                  } else if (isBuyingThisPackage) {
                    buttonText = t('subscription.redirecting')
                  }

                  return (
                    <div
                      key={pkg.id}
                      data-testid={`package-card-${pkg.maxLicenses ?? pkg.id}`}
                      data-marketing-selected={isMarketingSelection ? 'true' : 'false'}
                      className={cx(
                        'group relative flex min-h-full flex-col rounded-3xl border bg-white/90 p-5 shadow-[0_12px_36px_rgba(56,189,248,0.08)] backdrop-blur-xl transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_20px_56px_rgba(14,165,233,0.12)] gap-5',
                        isMarketingSelection
                          ? 'border-primary-400 ring-2 ring-primary-200'
                          : isCurrentPlan
                            ? 'border-primary-300 ring-2 ring-primary-100'
                            : 'border-primary-100/80 hover:border-primary-200'
                      )}
                    >
                      <div>
                        <div className='flex items-start justify-between gap-3'>
                          <div>
                            <span className='rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-700 ring-1 ring-primary-100'>
                              {t('common.planLabel', { tier: pkg.tier })}
                            </span>
                            <h3 className='mt-4 text-xl font-semibold text-primary-900'>
                              {pkg.name}
                            </h3>
                          </div>
                          {isCurrentPlan && (
                            <Badge title={t('subscription.activeBadge')} variant='success' />
                          )}
                        </div>

                        <div>
                          <span className='text-3xl font-semibold tracking-tight text-primary-900'>
                            {formatPrice(Number(pkg.amount), pkg.currency)}
                          </span>
                          {formatBillingInterval(pkg) && (
                            <span className='text-sm text-neutral-500'>
                              {formatBillingInterval(pkg)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div>
                        <p className='text-sm font-semibold leading-6 text-primary-900'>
                          {packageCopy.tagline}
                        </p>
                        <p className='mt-2 text-sm leading-6 text-neutral-500'>
                          {packageCopy.description}
                        </p>
                      </div>

                      <div className='grid grid-cols-1 gap-2'>
                        {packageCopy.highlights.map(highlight => (
                          <FeaturePill key={highlight} label={highlight} />
                        ))}
                      </div>

                      {isPendingChangeTarget && !isCurrentPlan && (
                        <div className='rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800'>
                          {t('common.scheduledChangeNotice')}
                        </div>
                      )}

                      <button
                        onClick={onClick}
                        disabled={buttonDisabled}
                        className={cx(
                          'mt-auto inline-flex w-full items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold transition duration-200 disabled:cursor-not-allowed disabled:opacity-60',
                          isCurrentPlan
                            ? 'border border-primary-200 bg-primary-50 text-primary-700'
                            : isDowngrade || isFreePackage
                              ? 'border border-primary-200 bg-white text-primary-700 hover:bg-primary-50'
                              : 'bg-gradient-to-r from-primary-400 to-info-500 text-white shadow-[0_14px_32px_rgba(56,189,248,0.22)] hover:-translate-y-0.5 hover:shadow-[0_20px_42px_rgba(56,189,248,0.26)]'
                        )}
                      >
                        {buttonText}
                      </button>
                    </div>
                  )
                })}

                <div className='group relative flex min-h-full flex-col gap-5 rounded-3xl border border-primary-100/80 bg-white/90 p-5 shadow-[0_12px_36px_rgba(56,189,248,0.08)] backdrop-blur-xl transition duration-200 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-[0_20px_56px_rgba(14,165,233,0.12)]'>
                  <div>
                    <div className='flex items-start justify-between gap-3'>
                      <div>
                        <span className='rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-700 ring-1 ring-primary-100'>
                          {t('common.planLabel', { tier: rankedPackages.length + 1 })}
                        </span>
                        <h3 className='mt-4 text-xl font-semibold text-primary-900'>
                          {t('subscription.enterpriseTitle')}
                        </h3>
                      </div>
                    </div>

                    <p className='mt-3 text-sm font-semibold leading-6 text-primary-900'>
                      {t('subscription.enterprise.tagline')}
                    </p>
                    <p className='mt-2 text-sm leading-6 text-neutral-500'>
                      {t('subscription.enterprise.description')}
                    </p>

                    <div className='mt-4'>
                      <span className='text-3xl font-semibold tracking-tight text-primary-900'>
                        {t('common.priceOnRequest')}
                      </span>
                    </div>
                  </div>

                  <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                    {(
                      [
                        'featureCustomLicenses',
                        'featureConsulting',
                        'featureBilling',
                        'featureSupport',
                      ] as const
                    ).map(feature => (
                      <FeaturePill key={feature} label={t(`subscription.enterprise.${feature}`)} />
                    ))}
                  </div>

                  <a
                    href={buildEnterpriseMailto(t('subscription.enterprise.mailSubject'))}
                    className='mt-auto inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-primary-400 to-info-500 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_14px_32px_rgba(56,189,248,0.22)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_20px_42px_rgba(56,189,248,0.26)]'
                  >
                    {t('subscription.requestQuote')}
                  </a>
                </div>
              </div>
            </section>

            <section className='space-y-4'>
              <SectionHeader
                title={t('subscription.invoicesTitle')}
                description={t('subscription.invoicesDescription')}
              />

              <SoftPanel className='overflow-hidden'>
                {invoicesLoading ? (
                  <div className='px-5 py-8 text-sm text-neutral-500'>
                    {t('subscription.loadingInvoices')}
                  </div>
                ) : invoices.length === 0 ? (
                  <div className='px-5 py-8 text-sm text-neutral-500'>
                    {t('subscription.noInvoices')}
                  </div>
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='w-full min-w-[640px]'>
                      <thead className='border-b border-primary-100 bg-primary-50/60'>
                        <tr>
                          <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-primary-900'>
                            {t('common.date')}
                          </th>
                          <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-primary-900'>
                            {t('common.amount')}
                          </th>
                          <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-primary-900'>
                            {t('common.status')}
                          </th>
                          <th className='px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-primary-900'>
                            {t('common.download')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-primary-50'>
                        {invoices.map(invoice => (
                          <tr
                            key={invoice.stripeInvoiceId}
                            className='transition hover:bg-primary-50/40'
                          >
                            <td className='px-5 py-4 text-sm text-neutral-700'>
                              {invoice.createdAt
                                ? formatSubscriptionDate(invoice.createdAt)
                                : t('common.dash')}
                            </td>
                            <td className='px-5 py-4 text-sm font-medium text-primary-900'>
                              {formatPrice(invoice.amount, invoice.currency)}
                            </td>
                            <td className='px-5 py-4'>
                              <Badge
                                title={subscriptionStatusToText(invoice.status)}
                                variant={getStatusVariant(invoice.status)}
                              />
                            </td>
                            <td className='px-5 py-4 text-right'>
                              {invoice.invoicePdfUrl || invoice.hostedInvoiceUrl ? (
                                <a
                                  href={invoice.invoicePdfUrl || invoice.hostedInvoiceUrl}
                                  target='_blank'
                                  rel='noreferrer'
                                  className='inline-flex items-center rounded-full bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 ring-1 ring-primary-100 transition hover:bg-primary-100'
                                >
                                  {t('subscription.download')}
                                </a>
                              ) : (
                                <span className='text-xs text-neutral-400'>{t('common.dash')}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SoftPanel>
            </section>
          </div>
        </div>
      </Container>
    </PageLayout>
  )
}
